import {
  AspectRatio,
  Box,
  Flex,
  Icon,
  Tag,
  Td,
  Text,
  Tooltip,
  Tr,
} from '@chakra-ui/react';
import { STOCK_UI } from '@diamond/shared/decimal-util-web';
import { LabelPromo, PromoPriceOrderDetail } from '@diamond/shared/ui';
import {
  buyerCurrencyFormatter,
  numberWithSeparator,
} from '@diamond/shared/utils';
import {
  CompiledProductDiscounts,
  processVoucherData,
  ProductWithPromo,
} from '@diamond/sol-buyer/utils';
import { OrderDetailPromoPrice } from '@diamond/sol-buyer-components';
import { dinero, toDecimal } from 'dinero.js';

import { CheckIconVoucher } from '../../../carts/Icons';
import { CheckboxOutlineBlankIcon } from '../../../checkout-new/icons';
import { Image } from '../../image';

const isValidNote = (note: unknown): note is string => {
  if (!note || typeof note !== 'string') return false;
  const trimmed = note.trim();
  return trimmed !== '' && trimmed !== 'null' && trimmed !== 'undefined';
};

type ProductRowProps = {
  product: ProductWithPromo;
  isVoucherMenuEnabled: boolean;
  divIdx: number;
  productIdx: number;
};

type ProductCalculations = {
  productPrice: number;
  confirmedPrice: number | undefined;
  subtotal: number;
  confirmedSubtotal: number | undefined;
  quantity: number;
  confirmedQuantity: number;
  shouldShowInitialPrice: boolean;
  shouldShowInitialQty: boolean;
  shouldShowInitialSubtotal: boolean;
  hasDiscount: boolean;
  discountPromo: CompiledProductDiscounts | null;
  hasVoucherDiscount: boolean;
  hasVoucherFreeGoods: boolean;
  voucherDiscountPercent: number | null;
  voucherDiscountAmount: number | null;
  voucherFinalUnitPrice: number | null;
};

const calculateProductPrice = (
  product: ProductWithPromo
): {
  productPrice: number;
  confirmedPrice: number | undefined;
  hasDiscount: boolean;
  discountPromo: CompiledProductDiscounts | null;
  hasVoucherDiscount: boolean;
  hasVoucherFreeGoods: boolean;
  voucherDiscountPercent: number | null;
  voucherDiscountAmount: number | null;
  voucherFinalUnitPrice: number | null;
} => {
  const voucherData = processVoucherData(product);
  const compiled_discount = product.compiled_discount;
  const hasDiscount = product.hasDiscount;
  const discountPromo: CompiledProductDiscounts | null = hasDiscount
    ? compiled_discount?.find(
        (promo: CompiledProductDiscounts) => promo.is_discount
      ) ?? null
    : null;

  let productPrice = product?.price ?? 0;

  if (voucherData.hasVoucherDiscount && voucherData.finalUnitPrice !== null) {
    productPrice = voucherData.finalUnitPrice;
  } else if (discountPromo?.price_after_discount) {
    productPrice = discountPromo.price_after_discount;
  }

  const confirmedPrice = product?.latest_price;

  return {
    productPrice,
    confirmedPrice,
    hasDiscount,
    discountPromo,
    hasVoucherDiscount: voucherData.hasVoucherDiscount,
    hasVoucherFreeGoods: voucherData.hasVoucherFreeGoods,
    voucherDiscountPercent: voucherData.discountPercent,
    voucherDiscountAmount: voucherData.discountAmount,
    voucherFinalUnitPrice: voucherData.finalUnitPrice,
  };
};

const calculateProductQuantity = (
  product: ProductWithPromo
): {
  quantity: number;
  confirmedQuantity: number;
} => {
  const quantity = Number(
    toDecimal(
      dinero({
        amount: Number(product?.quantity ?? 0),
        currency: STOCK_UI,
      })
    )
  );
  const confirmedQuantity = Number(
    toDecimal(
      dinero({
        amount: Number(product?.confirmed_quantity ?? 0),
        currency: STOCK_UI,
      })
    )
  );

  return { quantity, confirmedQuantity };
};

const calculateProductSubtotal = (
  product: ProductWithPromo,
  voucherData: ReturnType<typeof processVoucherData>
): {
  subtotal: number;
  confirmedSubtotal: number | undefined;
} => {
  let subtotal = product?.original_subtotal ?? 0;

  if (
    voucherData.hasVoucherDiscount &&
    voucherData.voucherDiscount?.final_subtotal
  ) {
    subtotal = voucherData.voucherDiscount.final_subtotal;
  }

  const confirmedSubtotal = product?.subtotal;

  return { subtotal, confirmedSubtotal };
};

const getProductCalculations = (
  product: ProductWithPromo
): ProductCalculations => {
  const voucherData = processVoucherData(product);
  const priceData = calculateProductPrice(product);
  const quantityData = calculateProductQuantity(product);
  const subtotalData = calculateProductSubtotal(product, voucherData);

  const shouldShowInitialPrice = product.is_price_diff ?? false;
  const shouldShowInitialQty = product.is_stock_diff ?? false;
  const shouldShowInitialSubtotal = product.subtotal_diff !== 0;

  return {
    ...priceData,
    ...quantityData,
    ...subtotalData,
    shouldShowInitialPrice,
    shouldShowInitialQty,
    shouldShowInitialSubtotal,
    hasVoucherDiscount: priceData.hasVoucherDiscount,
    hasVoucherFreeGoods: priceData.hasVoucherFreeGoods,
    voucherDiscountPercent: priceData.voucherDiscountPercent,
    voucherDiscountAmount: priceData.voucherDiscountAmount,
    voucherFinalUnitPrice: priceData.voucherFinalUnitPrice,
  };
};

export const ProductRow = ({
  product,
  isVoucherMenuEnabled,
  divIdx,
  productIdx,
}: ProductRowProps) => {
  const calculations = getProductCalculations(product);
  return (
    <Tr key={`product-row-${divIdx}-${productIdx}`}>
      <Td pt="0">
        <Flex w="full" gap="2">
          <Flex
            key={`product-image-${divIdx}-${productIdx}`}
            justifyContent="center"
            alignItems="center"
            rounded="lg"
            border="1px solid #F2F2F7"
            p="1"
          >
            <AspectRatio
              w="80px"
              ratio={1 / 1}
              flex="none"
              opacity={product.is_not_processed ? 0.7 : 1}
            >
              <Image
                fill
                src={product.product_images_medium?.[0]}
                alt={product.desc ?? ''}
                style={{
                  backgroundColor: 'white',
                  objectFit: 'contain',
                }}
              />
            </AspectRatio>
          </Flex>
          <Flex
            flexDir="column"
            justifyContent="start"
            alignItems="start"
            gap="1"
          >
            <Flex flexDir="row" gap="2" alignItems="center">
              <Text
                fontSize="xs"
                fontWeight="bold"
                textColor={product.is_not_processed ? 'gray' : 'black'}
              >
                {product.brand_desc}
              </Text>
              {isVoucherMenuEnabled && calculations.hasVoucherFreeGoods && (
                <CheckIconVoucher boxSize="16px" flexShrink={0} />
              )}
              {(product.free_good_data !== null ||
                calculations.hasVoucherFreeGoods) && (
                <LabelPromo type="free-item" />
              )}
              {product.hasCashback && <LabelPromo type="cashback" />}
              {product.is_not_processed ? (
                <Tag
                  ml={2}
                  borderRadius="full"
                  background="gray"
                  color="white"
                  fontWeight="bold"
                >
                  Dibatalkan
                </Tag>
              ) : null}
            </Flex>
            <Text
              fontSize="xs"
              textColor={product.is_not_processed ? 'gray' : 'black'}
            >
              {product.desc}
            </Text>
            {isValidNote(product.note) && (
              <Flex
                gap={2}
                align="start"
                px={0}
                py={1}
                bg="orange.50"
                borderRadius="md"
                alignItems="center"
                opacity={product.is_not_processed ? 0.7 : 1}
                maxW="220px"
              >
                <CheckboxOutlineBlankIcon
                  sx={{
                    fontSize: '20px',
                    color: '#8E8E93',
                  }}
                />
                {product.note.length > 30 ? (
                  <Tooltip label={product.note} hasArrow shouldWrapChildren>
                    <Text fontSize="xs" color="orange.800" noOfLines={1}>
                      {product.note}
                    </Text>
                  </Tooltip>
                ) : (
                  <Text fontSize="xs" color="orange.800">
                    {product.note}
                  </Text>
                )}
              </Flex>
            )}
          </Flex>
        </Flex>
      </Td>
      <Td pt="0">
        <Flex
          key={`product-qty-${divIdx}-${productIdx}`}
          minH="80px"
          w="100%"
          alignItems="center"
        >
          <Box textColor={product.is_not_processed ? 'gray' : 'black'}>
            <Text fontSize="md">
              {numberWithSeparator(
                calculations.shouldShowInitialQty
                  ? calculations.confirmedQuantity
                  : calculations.quantity
              )}
            </Text>
            {calculations.shouldShowInitialQty ? (
              <Box color="gray" mt={2}>
                <Text fontSize="sm">stok awal</Text>
                <Text fontSize="sm" fontWeight="semibold">
                  {numberWithSeparator(calculations.quantity)}
                </Text>
              </Box>
            ) : null}
          </Box>
        </Flex>
      </Td>
      <Td pt="0">
        <Flex
          key={`product-price-${divIdx}-${productIdx}`}
          minH="80px"
          w="100%"
          alignItems="center"
        >
          <Box>
            <Box mb="1">
              <OrderDetailPromoPrice
                product={product}
                isVoucherMenuEnabled={isVoucherMenuEnabled}
              />
            </Box>
            <Text
              fontSize="md"
              textColor={product.is_not_processed ? 'gray' : 'black'}
            >
              {buyerCurrencyFormatter(
                calculations.shouldShowInitialPrice
                  ? calculations.confirmedPrice
                  : calculations.productPrice
              )}
            </Text>
            {calculations.shouldShowInitialPrice ? (
              <Box color="gray" mt={2}>
                <Text fontSize="sm">harga awal</Text>
                <Text fontSize="sm" fontWeight="semibold">
                  {buyerCurrencyFormatter(calculations.productPrice)}
                </Text>
              </Box>
            ) : null}
          </Box>
        </Flex>
      </Td>
      <Td pt="0">
        <Flex
          key={`product-sub-total-${divIdx}-${productIdx}`}
          minH="80px"
          w="100%"
          alignItems="center"
          color={product.is_not_processed ? 'gray' : 'black'}
        >
          <Box>
            <Text fontSize="md">
              {buyerCurrencyFormatter(
                calculations.shouldShowInitialSubtotal
                  ? calculations.confirmedSubtotal
                  : calculations.subtotal
              )}
            </Text>
            {calculations.shouldShowInitialSubtotal ? (
              <Box color="gray" mt={2}>
                <Text fontSize="sm">total awal</Text>
                <Text fontSize="sm" fontWeight="semibold">
                  {buyerCurrencyFormatter(calculations.subtotal)}
                </Text>
              </Box>
            ) : null}
          </Box>
        </Flex>
      </Td>
    </Tr>
  );
};
